import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpEventType } from '@angular/common/http';
import { faCamera } from '@fortawesome/free-solid-svg-icons';
import * as piexif from 'piexifjs';
import { RaterFormService } from '../../raterform/raterform.service';
import { MessagerComponent } from '../../messager/messager/messager.component';
import { LoggingService } from '../../utils/logging.service';

export interface SelfiePromptData {
  job: any;
  form_id: number;
  usernamee: string;
}

// Capture + upload a selfie for one job from the schedule page. Lands the photo on the job's rater form
// (formpics 'selfie_pic') exactly like the form's own Selfie Photo button does.
@Component({
  selector: 'app-selfie-prompt',
  templateUrl: './selfie-prompt.component.html'
})
export class SelfiePromptComponent {

  facamera = faCamera;
  uploading = false;
  percent = 0;
  uploadedUrl = '';
  errorText = '';

  constructor(
    public dialogRef: MatDialogRef<SelfiePromptComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SelfiePromptData,
    private raterFormService: RaterFormService,
    private messager: MessagerComponent,
    private log: LoggingService
  ) {}

  openCamera(): void {
    document.getElementById('selfie_prompt_input').click();
  }

  close(): void {
    this.dialogRef.close({ uploaded: !!this.uploadedUrl, url: this.uploadedUrl });
  }

  onFilePicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file: File = input.files && input.files[0];
    if(!file){ return; }

    const fileExt = (file.name.split('.').pop() || '').toLowerCase();
    if(fileExt !== 'jpg' && fileExt !== 'jpeg'){
      this.messager.openSnackBar('Only JPG and JPEG images are allowed!', '', 'errorAlert');
      input.value = '';
      return;
    }

    // Same S3 key convention as the rater form; the per-job selfie lookup reads the job id off it.
    const key = this.data.job.job_id + '_selfie_pic-' + Date.now();
    const url = 'https://inspexn.s3.amazonaws.com/' + key + '.' + fileExt;

    this.errorText = '';
    this.uploading = true;
    this.percent = 0;
    this.dialogRef.disableClose = true;

    resizeWithExif(file, 1024).then(({ exif, resizedFile }) => {
      this.raterFormService.addExifData({ imagename: key, exifdata: exif }).subscribe(() => {}, () => {});

      this.raterFormService.uploadRaterPicWithProgress(resizedFile, key).subscribe({
        next: (evt) => {
          if(evt.type === HttpEventType.UploadProgress && evt.total){
            this.percent = Math.round(100 * evt.loaded / evt.total);
          }else if(evt.type === HttpEventType.Response){
            if(evt.body && evt.body.Error){
              this.failUpload('Upload failed: ' + (evt.body.Message || 'server rejected the file.'));
              return;
            }
            this.recordUpload(url, key);
          }
        },
        error: (err) => {
          let userMessage = '';
          if(err.status === 0){
            userMessage = navigator.onLine
              ? 'Upload failed due to a network error (connection lost or blocked). Please try again.'
              : 'Upload failed: no network connection. Please reconnect and try again.';
          }else if(err.status >= 500){
            userMessage = `Upload failed: server error (${err.status}). Please try again later.`;
          }else{
            userMessage = `Upload failed (${err.status}): ${err.statusText || 'Unexpected error.'}`;
          }
          this.failUpload(userMessage);
        }
      });
    }).catch((err) => {
      this.failUpload('Error processing the image: ' + (err && err.message ? err.message : err));
    });
  }

  private recordUpload(url: string, key: string): void {
    const form_id = this.data.form_id;
    const who = this.data.usernamee || 'Inspector';
    this.raterFormService.insertpicsToFormTable(form_id, { field_name: 'selfie_pic', image_url: url }).subscribe(
      res => {
        if(res && res.Error){
          this.failUpload('Picture uploaded but could not be saved to the form: ' + res.Message);
          return;
        }
        this.raterFormService.updateRFLog('selfie_pic_pic', form_id, key, who).subscribe(() => {}, () => {});
        this.uploading = false;
        this.percent = 100;
        this.uploadedUrl = url;
        this.dialogRef.disableClose = false;
        this.messager.openSnackBar('Selfie saved to the rater form.', '', 'successAlert');
      }, err => {
        this.failUpload('Picture uploaded but could not be saved to the form: ' + (err.message || err));
      });
  }

  private failUpload(message: string): void {
    this.uploading = false;
    this.percent = 0;
    this.errorText = message;
    this.dialogRef.disableClose = false;
    this.log.error((this.data.usernamee || 'Inspector') + ' selfie upload failed for job id-' + this.data.job.job_id
      + ' with location_id: ' + this.data.job.location_id + ' - ' + message);
    this.messager.openSnackBar('Selfie upload failed.', '', 'errorAlert');
  }
}

// Copy of RaterformComponent.resizeWithExif without its form-scoped error logging. Reads EXIF off the
// original before the canvas resize strips it.
function resizeWithExif(file: File, maxWidth: number): Promise<{ exif: any; resizedFile: File }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const img = new Image();
    let canvas: HTMLCanvasElement | null = null;

    const cleanup = () => {
      reader.onload = null;
      reader.onerror = null;
      img.onload = null;
      img.onerror = null;
      if(canvas){
        canvas.width = 0;
        canvas.height = 0;
        canvas = null;
      }
    };

    reader.onerror = () => {
      cleanup();
      reject(new Error('Failed to read image data'));
    };

    reader.onload = () => {
      const dataUrl = reader.result as string;
      let exifObj: any;
      try{
        exifObj = piexif.load(dataUrl);
      }catch(e){
        exifObj = {};
      }

      img.onload = () => {
        const scale = img.naturalWidth > maxWidth ? maxWidth / img.naturalWidth : 1;
        const targetWidth = Math.round(img.naturalWidth * scale);
        const targetHeight = Math.round(img.naturalHeight * scale);

        canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');
        if(!ctx){
          cleanup();
          reject(new Error('Canvas 2D context not available'));
          return;
        }
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
        canvas.toBlob((blob) => {
          if(!blob){
            cleanup();
            reject(new Error('Canvas toBlob failed'));
            return;
          }
          const resizedFile = new File([blob], file.name, { type: 'image/jpeg' });
          cleanup();
          resolve({ exif: exifObj, resizedFile });
        }, 'image/jpeg', 0.8);
      };

      img.onerror = () => {
        cleanup();
        reject(new Error('Failed to decode image'));
      };

      img.src = dataUrl;
    };

    reader.readAsDataURL(file);
  });
}
